// ============================================================================
// AM entry screen — the reads. Run B Phase 2, 2026-08-18.
//
// Two hooks, both read-only:
//   useProviderHistory()  — the whole (sub x service x facility) aggregate, once
//                           per screen, so ranking a picker is a sort in memory
//                           rather than a query per keystroke.
//   usePriorWorkHere()    — what this facility was billed and what the sub was
//                           paid the last few times this same service was done.
//
// Neither writes anything, and neither decides anything: the ranking rule lives
// in lib/entry-derive.ts where it is one named constant Jack can rule on.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { capabilitiesForServiceCode } from '../../subcontractors/capabilities';
import type { ProviderExperience, RankableProvider } from '../lib/entry-derive';
import type { SmartSelectRow } from '../../../components/smart-select';
import { fmtBareDate } from '../../../shared/format';
import { isProviderSelectable } from '../lib/sub-picker';
import { subStatusLabel } from '../../subcontractors/stages';

interface HistoryRow {
  provider_id: string;
  service_code: string;
  facility_id: string | null;
  jobs: number;
  last_service_date: string | null;
}

export interface ProviderHistoryResult {
  rows: HistoryRow[];
  isLoading: boolean;
  error: string | null;
  experienceFor: (
    providers: Array<{ provider_id: string; provider_name: string | null }>,
    serviceCode: string,
    facilityId: string | null,
  ) => RankableProvider[];
}

export function useProviderHistory(): ProviderHistoryResult {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('v_provider_service_history')
          .select('provider_id, service_code, facility_id, jobs, last_service_date');
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as unknown as HistoryRow[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load subcontractor history.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const byProvider = new Map<string, HistoryRow[]>();
    for (const r of rows) {
      const list = byProvider.get(r.provider_id);
      if (list) list.push(r);
      else byProvider.set(r.provider_id, [r]);
    }

    // Every active provider is returned, including those with no history at all
    // — a sub who has never done this work is still assignable, just last.
    const experienceFor = (
      providers: Array<{ provider_id: string; provider_name: string | null }>,
      serviceCode: string,
      facilityId: string | null,
    ): RankableProvider[] => {
      const wanted = capabilitiesForServiceCode(serviceCode);
      const wantedCap = wanted.length > 0 && wanted[0] !== 'Other' ? wanted[0] : null;
      return providers.map((p) => {
        const mine = byProvider.get(p.provider_id) ?? [];
        let jobsThisServiceHere = 0;
        let jobsThisServiceAnywhere = 0;
        let jobsAtThisFacility = 0;
        let hasCapability = false;
        let lastServiceDate: string | null = null;
        for (const r of mine) {
          const sameService = !!serviceCode && r.service_code === serviceCode;
          const sameFacility = !!facilityId && r.facility_id === facilityId;
          if (sameService) jobsThisServiceAnywhere += r.jobs;
          if (sameFacility) jobsAtThisFacility += r.jobs;
          if (sameService && sameFacility) jobsThisServiceHere += r.jobs;
          // Capability comes from what this sub has actually been recorded
          // doing, through the same map the subcontractor directory uses.
          // "Other" proves nothing, so it never grants a capability.
          if (!hasCapability && wantedCap) {
            if (capabilitiesForServiceCode(r.service_code).includes(wantedCap)) hasCapability = true;
          }
          if (r.last_service_date && (!lastServiceDate || r.last_service_date > lastServiceDate)) {
            lastServiceDate = r.last_service_date;
          }
        }
        const experience: ProviderExperience = {
          jobsThisServiceHere,
          jobsThisServiceAnywhere,
          jobsAtThisFacility,
          hasCapability,
          lastServiceDate,
        };
        return { provider_id: p.provider_id, provider_name: p.provider_name, experience };
      });
    };

    return { rows, isLoading, error, experienceFor };
  }, [rows, isLoading, error]);
}

// ---------------------------------------------------------------------------

export interface PriorWorkRow {
  service_line_id: string;
  project_id: string;
  date: string | null;
  client_rate: number | null;
  sub_rate: number | null;
  provider_name: string | null;
  scope_text: string | null;
}

/**
 * Jack's open question is whether the SUB PAY half of prior work belongs on an
 * AM's screen at all. It is shown because the mockup he approved shows it
 * ("billed $685 · paid $480"), and it is one flag away from being hidden.
 */
export const SHOW_SUB_PAY_IN_PRIOR_WORK = true;

/** The last few times this service was done at this facility — billed and paid. */
export function usePriorWorkHere(
  facilityId: string | null,
  serviceCode: string | null,
): { rows: PriorWorkRow[]; isLoading: boolean; error: string | null; hasFetched: boolean } {
  const [rows, setRows] = useState<PriorWorkRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId || !serviceCode) {
      setRows([]);
      setHasFetched(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('project_services')
          .select(
            `service_line_id, project_id, scope_text, client_rate, sub_rate,
             service_date_actual, service_date_planned,
             projects!inner ( facility_id, completion_date, start_date ),
             provider:service_providers!project_services_assigned_partner_id_fkey ( partner_name )`,
          )
          .eq('service_code', serviceCode)
          .eq('projects.facility_id', facilityId)
          .limit(25);
        if (cancelled) return;
        if (e) throw e;
        type Row = {
          service_line_id: string;
          project_id: string;
          scope_text: string | null;
          client_rate: number | null;
          sub_rate: number | null;
          service_date_actual: string | null;
          service_date_planned: string | null;
          projects: { completion_date: string | null; start_date: string | null } | null;
          provider: { partner_name: string | null } | null;
        };
        const mapped = ((data ?? []) as unknown as Row[]).map((r) => ({
          service_line_id: r.service_line_id,
          project_id: r.project_id,
          date:
            r.service_date_actual ??
            r.service_date_planned ??
            r.projects?.completion_date ??
            r.projects?.start_date ??
            null,
          client_rate: r.client_rate,
          sub_rate: r.sub_rate,
          provider_name: r.provider?.partner_name ?? null,
          scope_text: r.scope_text,
        }));
        mapped.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
        setRows(mapped.slice(0, 3));
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load prior work at this facility.'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasFetched(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, serviceCode]);

  return { rows, isLoading, error, hasFetched };
}

// ---------------------------------------------------------------------------

/** How many market project subs the card lists before search and "Show all". */
export const MARKET_PROJECT_SUBS_CAP = 8;

/**
 * Most filled project start dates are this day — a backfill floor, not a date
 * anybody did work (STATE). ⛔ Never shown as "last here"; the row shows the
 * service alone instead.
 */
const PROJECT_DATE_BACKFILL_FLOOR = '2026-01-01';

export type ProjectSubGroup = { key: string; label: string; rows: SmartSelectRow[] };

interface SubRef {
  partner_name: string | null;
  provider_status: string | null;
  provider_class: string | null;
  coverage_markets: string[] | null;
}

interface ProjectLineRow {
  assigned_partner_id: string;
  project_id: string;
  service_code: string | null;
  service_date_actual: string | null;
  created_at: string | null;
  service_catalog: { display_name: string | null } | null;
  projects: { facility_id: string; completion_date: string | null } | null;
  provider: SubRef | null;
}

/** Distinct subs from project lines, most recent first, one row per sub. */
function latestPerSub(lines: ProjectLineRow[]): SmartSelectRow[] {
  const keyed = lines
    .filter((l) => l.provider && l.provider.provider_class === 'Field_Service')
    .map((l) => {
      const date = l.service_date_actual ?? l.projects?.completion_date ?? null;
      return { l, date, sortKey: date ?? l.created_at?.slice(0, 10) ?? '' };
    })
    .sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  const seen = new Set<string>();
  const out: SmartSelectRow[] = [];
  for (const { l, date } of keyed) {
    if (seen.has(l.assigned_partner_id)) continue;
    seen.add(l.assigned_partner_id);
    const service = l.service_catalog?.display_name ?? l.service_code ?? null;
    const shownDate = date && date !== PROJECT_DATE_BACKFILL_FLOOR ? fmtBareDate(date) : null;
    out.push({
      id: l.assigned_partner_id,
      name: l.provider?.partner_name ?? l.assigned_partner_id,
      subtitle: [service ? `Last: ${service}` : null, shownDate].filter(Boolean).join(' · ') || null,
      // provider_status rides along so isProviderSelectable reads it the same
      // way it reads SmartSelect's own rows.
      raw: { provider_id: l.assigned_partner_id, ...l.provider } as unknown as Record<string, unknown>,
    });
  }
  return out;
}

const PROJECT_LINE_SELECT = `assigned_partner_id, project_id, service_code, service_date_actual, created_at,
   service_catalog ( display_name ),
   projects!inner ( facility_id, completion_date ),
   provider:service_providers!project_services_assigned_partner_id_fkey ( partner_name, provider_status, provider_class, coverage_markets )`;

/**
 * The project card's sub picker groups — Ben's ruling, 2026-10-01 (item 654).
 * Top to bottom:
 *   1. Has done project work here — any service, this facility, not this project
 *   2. Works this building — Active nightly assignments here (the old scope)
 *   3. Project subs in this market — other facilities in the same
 *      market_region, capped at MARKET_PROJECT_SUBS_CAP
 * A sub appears ONCE, in the highest group it qualifies for. A sub who may not
 * be newly chosen (Inactive, Merged) is left out of the groups; a line that
 * already names one still shows the name, because SmartSelect resolves a saved
 * value by id whatever list is showing (TCSD-0163).
 *
 * One fetch per card, keyed on facility and project — never per keystroke.
 * No pay figures in any subtitle: the picker is the wrong place (TCSD-0093
 * permits them; the prior-work panel above already shows them).
 */
export function useProjectSubGroups(
  facilityId: string | null,
  projectId: string | null,
): ProjectSubGroup[] {
  const [groups, setGroups] = useState<ProjectSubGroup[]>([]);

  useEffect(() => {
    if (!facilityId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [facRes, hereRes, nightlyRes] = await Promise.all([
          supabase.from('facilities').select('market_region').eq('facility_id', facilityId).maybeSingle(),
          supabase
            .from('project_services')
            .select(PROJECT_LINE_SELECT)
            .eq('projects.facility_id', facilityId)
            .neq('line_role', 'chargeback')
            .not('assigned_partner_id', 'is', null),
          supabase
            .from('service_assignments')
            .select('provider_id')
            .eq('facility_id', facilityId)
            .eq('assignment_status', 'Active'),
        ]);
        if (facRes.error) throw facRes.error;
        if (hereRes.error) throw hereRes.error;
        if (nightlyRes.error) throw nightlyRes.error;

        const hereLines = ((hereRes.data ?? []) as unknown as ProjectLineRow[]).filter(
          (l) => !projectId || l.project_id !== projectId,
        );
        const here = latestPerSub(hereLines).filter(isProviderSelectable);
        const taken = new Set(here.map((r) => r.id));

        const nightlyIds = [
          ...new Set(
            ((nightlyRes.data ?? []) as Array<{ provider_id: string | null }>)
              .map((r) => r.provider_id)
              .filter((v): v is string => Boolean(v) && !taken.has(v as string)),
          ),
        ];
        let nightly: SmartSelectRow[] = [];
        if (nightlyIds.length > 0) {
          const { data, error } = await supabase
            .from('service_providers')
            .select('provider_id, partner_name, coverage_markets, provider_status')
            .in('provider_id', nightlyIds)
            .eq('provider_class', 'Field_Service')
            .order('partner_name');
          if (error) throw error;
          type P = { provider_id: string; partner_name: string | null; coverage_markets: string[] | null; provider_status: string | null };
          nightly = ((data ?? []) as P[])
            .map((p) => ({
              id: p.provider_id,
              name: p.partner_name ?? p.provider_id,
              subtitle: [(p.coverage_markets ?? []).join(' · '), subStatusLabel(p.provider_status)].filter(Boolean).join(' · ') || null,
              raw: p as unknown as Record<string, unknown>,
            }))
            .filter(isProviderSelectable);
        }
        for (const r of nightly) taken.add(r.id);

        let market: SmartSelectRow[] = [];
        const region = (facRes.data as { market_region: string | null } | null)?.market_region ?? null;
        if (region) {
          const { data: facs, error: facsErr } = await supabase
            .from('facilities')
            .select('facility_id')
            .eq('market_region', region)
            .neq('facility_id', facilityId);
          if (facsErr) throw facsErr;
          const ids = ((facs ?? []) as Array<{ facility_id: string }>).map((f) => f.facility_id);
          if (ids.length > 0) {
            const { data, error } = await supabase
              .from('project_services')
              .select(PROJECT_LINE_SELECT)
              .in('projects.facility_id', ids)
              .neq('line_role', 'chargeback')
              .not('assigned_partner_id', 'is', null);
            if (error) throw error;
            market = latestPerSub((data ?? []) as unknown as ProjectLineRow[])
              .filter(isProviderSelectable)
              .filter((r) => !taken.has(r.id))
              .slice(0, MARKET_PROJECT_SUBS_CAP);
          }
        }

        if (cancelled) return;
        setGroups([
          { key: 'project-here', label: 'Has done project work here', rows: here },
          { key: 'works-building', label: 'Works this building', rows: nightly },
          { key: 'project-market', label: 'Project subs in this market', rows: market },
        ]);
      } catch (err) {
        if (cancelled) return;
        // The groups are an ordering aid. Without them the picker still has its
        // scoped list, search and "Show all", so this never blocks the card.
        console.error('[project-sub-groups] load failed', errorMessage(err, 'unknown error'));
        setGroups([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, projectId]);

  return groups;
}
